/**
 * The dialog window's send/receive plumbing for the Create-slides channel.
 *
 * Kept separate from createSlidesDialogChannel.ts (which is pure
 * parse/serialise, and is imported by the PANE too) so the pane never pulls
 * in code that touches `Office.context.ui.messageParent`. Mirrors
 * asset-library/services/dialogToPaneMessaging.ts, whose helpers are typed to
 * that feature's own message union and so can't be shared.
 */

import { logEvent, logError, scrubMessage } from "../../../../../telemetry";
import {
  serialiseCreateSlidesMessage,
  type CreateSlidesDialogToPane,
} from "./createSlidesDialogChannel";

const CREATE_SLIDES_CONNECTOR = "deckorate";

/** Whether this window can talk back to the pane at all. */
export function canMessageParent(): boolean {
  return typeof Office !== "undefined" && typeof Office.context?.ui?.messageParent === "function";
}

/** Whether the pane can push messages INTO this window (DialogApi 1.2+) —
 *  required for anything the wizard has to ASK the pane for (the outline, the
 *  template library), as opposed to just posting at the end. */
export function canReceiveFromParent(): boolean {
  return typeof Office !== "undefined" && typeof Office.context?.ui?.addHandlerAsync === "function";
}

/** Sends `message` to the pane. Returns false (with a telemetry trace) when
 *  there's no channel to send it on — the caller decides how to degrade. */
export function postToPane(message: CreateSlidesDialogToPane, reason: string): boolean {
  if (!canMessageParent()) {
    logEvent("connector.action.invoke", "warn", {
      connectorSystemName: CREATE_SLIDES_CONNECTOR,
      reason: "create-slides-message-parent-unavailable",
    });
    return false;
  }
  logEvent("connector.action.invoke", "info", { connectorSystemName: CREATE_SLIDES_CONNECTOR, reason });
  try {
    Office.context.ui.messageParent(serialiseCreateSlidesMessage(message));
    return true;
  } catch (error) {
    // The dialog window (or its parent) can be mid-teardown when this fires;
    // messageParent can throw rather than silently no-op in that case.
    logError(
      "error.connector.failure",
      {
        type: error instanceof Error ? error.name : "MessageParentError",
        scrubbedMessage: scrubMessage(error instanceof Error ? error.message : String(error)),
      },
      { connectorSystemName: CREATE_SLIDES_CONNECTOR, reason: "create-slides-message-parent-threw" },
    );
    return false;
  }
}
